import React, { useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  Stepper, Step, StepLabel, Box, Typography, TextField,
  MenuItem, Divider, Chip, CircularProgress, Alert
} from '@mui/material';
import { CloudUpload, WarningAmber, FilePresent, Inventory2 } from '@mui/icons-material';
import { ReleaseNotesViewer } from './ReleaseNotesViewer';

const API_BASE = 'https://console.dreamtec.io';
const steps = ['Upload Build', 'Release Details', 'Review & Submit'];

// .apk / .aab = raw Android build, .pcg = SOTI Package Studio package
const ALLOWED = ['apk', 'aab', 'pcg'];
const NOTES_MAX = 4000;

const getExt = (file) => file?.name.split('.').pop().toLowerCase() ?? '';

const EMPTY_FORM = {
  version: '',
  versionCode: '',
  environment: 'PRODUCTION',
  customer_type: 'FUEL',
  notes: '',
};

export const CreateReleaseModal = ({ open, onClose, onSuccess }) => {
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM);

  const ext = getExt(selectedFile);
  const isPackage = ext === 'pcg';

  const reset = () => {
    setSelectedFile(null);
    setFormData(EMPTY_FORM);
    setActiveStep(0);
    setError(null);
  };

  const handleClose = () => {
    if (loading) return;
    reset();
    onClose();
  };

  const handleNext = () => setActiveStep((prev) => prev + 1);
  const handleBack = () => {
    setError(null);
    setActiveStep((prev) => prev - 1);
  };

  // ─── VALIDATION ───
  const validateAndSetFile = (file) => {
    setError(null);
    if (!file) return;

    if (!ALLOWED.includes(getExt(file))) {
      setError('Unsupported file format. Use an .apk, .aab, or SOTI .pcg package.');
      setSelectedFile(null);
      return;
    }
    setSelectedFile(file);
  };

  // ─── DRAG AND DROP ───
  const handleDragOver = (e) => { e.preventDefault(); e.stopPropagation(); setIsDragging(true); };
  const handleDragLeave = (e) => { e.preventDefault(); e.stopPropagation(); setIsDragging(false); };
  const handleDrop = (e) => {
    e.preventDefault(); e.stopPropagation(); setIsDragging(false);
    const files = e.dataTransfer.files;
    if (files?.length) validateAndSetFile(files[0]);
  };
  const handleFileSelect = (e) => {
    validateAndSetFile(e.target.files[0]);
    e.target.value = ''; // allow re-selecting the same file
  };

  const handleFinish = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      const user = localStorage.getItem('user');
      const userName = user ? JSON.parse(user).full_name : 'Unknown User';

      const data = new FormData();
      data.append('version_label', formData.version.trim());
      data.append('version_code', formData.versionCode);
      data.append('environment', formData.environment);
      data.append('release_notes', formData.notes);
      data.append('customer_type', formData.customer_type);
      data.append('uploaded_by', userName);
      data.append('apk_file', selectedFile); // backend accepts .apk/.aab/.pcg on this field

      const response = await fetch(`${API_BASE}/app-versions`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: data,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to upload release');
      }

      setLoading(false);
      reset();
      onSuccess();
      onClose();
    } catch (err) {
      setLoading(false);
      setError(err.message);
    }
  };

  const isNextDisabled = () => {
    if (activeStep === 0) return !selectedFile;
    if (activeStep === 1) {
      return !formData.version.trim() || !formData.versionCode || !formData.notes.trim()
        || formData.notes.length > NOTES_MAX;
    }
    return false;
  };

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Box sx={{ py: 4, textAlign: 'center' }}>
            <Box
              component="label"
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              sx={{
                border: error ? '2px dashed #ef4444' : (isDragging ? '2px dashed #2F727C' : '2px dashed #e2e8f0'),
                p: 6, borderRadius: 4,
                bgcolor: isDragging ? 'rgba(47, 114, 124, 0.04)' : '#f8fafc',
                cursor: 'pointer', display: 'block', transition: 'all 0.2s ease',
                '&:hover': { borderColor: '#2F727C', bgcolor: '#f1f5f9' },
              }}
            >
              <input type="file" accept=".apk,.aab,.pcg" hidden onChange={handleFileSelect} />

              {selectedFile
                ? (isPackage
                    ? <Inventory2 sx={{ fontSize: 52, color: '#2F727C', mb: 2 }} />
                    : <FilePresent sx={{ fontSize: 52, color: '#2F727C', mb: 2 }} />)
                : <CloudUpload sx={{ fontSize: 52, color: isDragging ? '#2F727C' : '#94a3b8', mb: 2 }} />}

              <Typography variant="h6" sx={{ color: '#1A3A3F', fontWeight: 700 }}>
                {selectedFile
                  ? (isPackage ? 'SOTI Package Attached' : 'Build Attached')
                  : (isDragging ? 'Drop to Upload' : 'Upload Build or Package')}
              </Typography>

              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {selectedFile ? selectedFile.name : 'Drag and drop or click to browse (.apk, .aab, .pcg)'}
              </Typography>

              {selectedFile && (
                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', mt: 1 }}>
                  <Chip label={ext.toUpperCase()} size="small"
                    sx={{ fontWeight: 800, borderRadius: 1, bgcolor: isPackage ? '#F6B93B' : '#e2e8f0' }} />
                  <Chip label={`${(selectedFile.size / (1024 * 1024)).toFixed(2)} MB`} size="small"
                    sx={{ fontWeight: 800, bgcolor: 'rgba(47, 114, 124, 0.1)', color: '#2F727C', borderRadius: 1 }} />
                </Box>
              )}
            </Box>

            {isPackage && (
              <Alert severity="info" sx={{ mt: 2, borderRadius: 2, textAlign: 'left' }}>
                .pcg packages are deployed through SOTI MobiControl. Devices can't download them directly, so the self-update check only serves APKs.
              </Alert>
            )}

            {error && (
              <Alert severity="error" icon={<WarningAmber />} sx={{ mt: 2, borderRadius: 2, fontWeight: 600 }}>
                {error}
              </Alert>
            )}
          </Box>
        );

      case 1:
        return (
          <Box sx={{ py: 2, display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField label="Version Name" required fullWidth value={formData.version}
                onChange={(e) => setFormData({ ...formData, version: e.target.value })} />
              <TextField label="Version Code" type="number" required fullWidth value={formData.versionCode}
                onChange={(e) => setFormData({ ...formData, versionCode: e.target.value })} />
            </Box>
            <TextField select label="Customer" fullWidth value={formData.customer_type}
              onChange={(e) => setFormData({ ...formData, customer_type: e.target.value })}>
              <MenuItem value="FUEL">Fuel</MenuItem>
              <MenuItem value="FOOD">Food</MenuItem>
            </TextField>
            <TextField select label="Environment" fullWidth value={formData.environment}
              onChange={(e) => setFormData({ ...formData, environment: e.target.value })}>
              <MenuItem value="PRODUCTION">Production</MenuItem>
              <MenuItem value="TEST">Test (Beta)</MenuItem>
            </TextField>
            <TextField
              label="Release Notes"
              required
              multiline
              minRows={5}
              maxRows={12} // grows, then scrolls
              fullWidth
              placeholder={'- Fixed pump sync timeout\n- Added offline queue for receipts'}
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              error={formData.notes.length > NOTES_MAX}
              helperText={`One change per line. ${formData.notes.length}/${NOTES_MAX}`}
              InputProps={{ sx: { fontFamily: 'inherit', lineHeight: 1.6 } }}
            />
          </Box>
        );

      case 2:
        return (
          <Box sx={{ py: 3 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>Build Summary</Typography>
            <Box sx={{ bgcolor: '#f8fafc', p: 3, borderRadius: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Row label="File:"><Typography sx={{ fontWeight: 600, wordBreak: 'break-all', textAlign: 'right' }}>{selectedFile?.name}</Typography></Row>
              <Row label="Format:"><Typography sx={{ fontWeight: 700, color: '#2F727C' }}>{isPackage ? 'SOTI PACKAGE (PCG)' : ext.toUpperCase()}</Typography></Row>
              <Row label="Version:"><Typography sx={{ fontWeight: 700 }}>{formData.version} ({formData.versionCode})</Typography></Row>
              <Row label="Customer:"><Typography sx={{ fontWeight: 700 }}>{formData.customer_type}</Typography></Row>
              <Row label="Target:">
                <Chip label={formData.environment} size="small"
                  sx={{ fontWeight: 700, bgcolor: formData.environment === 'PRODUCTION' ? '#F6B93B' : '#e2e8f0' }} />
              </Row>
              <Divider sx={{ my: 1 }} />
              <Typography variant="caption" sx={{ fontWeight: 800, color: '#64748b', textTransform: 'uppercase' }}>
                Release Notes
              </Typography>
              <ReleaseNotesViewer notes={formData.notes} maxHeight={180} />
            </Box>
            {error && (
              <Alert severity="error" icon={<WarningAmber />} sx={{ mt: 2, borderRadius: 2, fontWeight: 600 }}>
                {error}
              </Alert>
            )}
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm" PaperProps={{ sx: { borderRadius: 4 } }}>
      <DialogTitle sx={{ fontWeight: 900, p: 3, color: '#1A3A3F' }}>Release Configuration</DialogTitle>
      <DialogContent dividers sx={{ px: 4 }}>
        <Stepper activeStep={activeStep} sx={{ py: 2 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel sx={{ '& .MuiStepLabel-label': { fontSize: '0.75rem', fontWeight: 800 } }}>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
        {renderStepContent(activeStep)}
      </DialogContent>
      <DialogActions sx={{ p: 3 }}>
        <Button onClick={handleClose} disabled={loading} sx={{ fontWeight: 700 }} color="error">Cancel</Button>
        <Box sx={{ flex: '1 1 auto' }} />
        {activeStep !== 0 && (
          <Button onClick={handleBack} disabled={loading} sx={{ fontWeight: 700 }} color="primary">Back</Button>
        )}
        <Button
          variant="contained"
          onClick={activeStep === steps.length - 1 ? handleFinish : handleNext}
          disabled={isNextDisabled() || loading}
          color="primary"
        >
          {loading
            ? <CircularProgress size={24} color="inherit" />
            : (activeStep === steps.length - 1 ? (isPackage ? 'Publish Package' : 'Publish Build') : 'Next')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const Row = ({ label, children }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
    <Typography color="text.secondary">{label}</Typography>
    {children}
  </Box>
);